import { useState } from 'react'
import { router } from '@inertiajs/react'
import { Button, Input, Modal, Select, Tooltip, message } from 'antd'
import AiQuotaBar from './AiQuotaBar'
import type { AiCredential, AiModelOption, AiProps, AiProvider } from '../../types/ai'

// /settings 第三张卡片。落点与 ApiTokensManager 同一个槽位。
//
// 三块内容：配额条（AiQuotaBar）、四个供应商槽位、近 30 天用量小结。

const PROVIDER_META: Record<AiProvider, { name: string; badge: string; badgeClass: string; hint: string }> = {
  openai: { name: 'OpenAI', badge: 'O', badgeClass: 'bg-emerald-600', hint: '从 platform.openai.com 拿 API key。' },
  anthropic: { name: 'Anthropic', badge: 'A', badgeClass: 'bg-orange-500', hint: '从 console.anthropic.com 拿 API key。' },
  gemini: { name: 'Gemini', badge: 'G', badgeClass: 'bg-blue-500', hint: '用 Google AI Studio 的 API key。' },
  // 千问与兼容口的区别：地址是固定的（阿里就这一个端点），所以它有模型下拉、
  // 不让填地址 —— 走的是和原生三家一样的那条路。
  qwen: {
    name: '通义千问',
    badge: '通',
    badgeClass: 'bg-violet-600',
    hint: '从阿里云百炼控制台拿 API key（sk- 开头）。地址是固定的，不用填。',
  },
  custom: {
    name: '其他（OpenAI 兼容）',
    badge: '⋯',
    badgeClass: 'bg-gray-500',
    hint: 'DeepSeek / OpenRouter / 硅基流动 / 本地 Ollama —— 填 API 地址 + key + 模型 id。',
  },
}

// 错误码 → 药丸文案。与服务端 Ai::Gateway::ERROR_CODES 同一套口径。
const ERROR_LABEL: Record<string, string> = {
  unauthorized: '上次 401',
  payment_required: '账户欠费',
  rate_limit: '上次 429',
  context_length: '上下文超长',
  upstream: '上游 5xx',
  bad_request: '请求被拒',
  model_not_found: '模型不存在',
  unknown: '上次失败',
}

const money = (value: number) => (value === 0 ? '$0' : value < 0.01 ? '<$0.01' : `$${value.toFixed(2)}`)

const when = (iso: string | null) => {
  if (!iso) return '从未'
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.round(diff / 60000)
  if (mins < 1) return '刚刚'
  if (mins < 60) return `${mins} 分钟前`
  const hours = Math.round(mins / 60)
  if (hours < 24) return `${hours} 小时前`
  return `${Math.round(hours / 24)} 天前`
}

// 近 30 天按天堆叠柱。两色对应两条 key 来源，与配额条、后台同一套配色。
function UsageChart({ series }: { series: AiProps['quota']['daily_series'] }) {
  const max = Math.max(0.0001, ...series.map((d) => d.own + d.system))
  const width = 620
  const gap = 3
  const barWidth = (width - gap * (series.length - 1)) / series.length
  const base = 66

  return (
    <svg viewBox={`0 0 ${width} 74`} preserveAspectRatio="none" className="block h-[74px] w-full"
         aria-label="近 30 天每日 AI 成本">
      <line x1="0" y1={base} x2={width} y2={base} stroke="currentColor" className="text-gray-200" strokeWidth="1" />
      {series.map((day, index) => {
        const x = index * (barWidth + gap)
        const ownH = Math.round((day.own / max) * (base - 4))
        const sysH = Math.round((day.system / max) * (base - 4))
        return (
          <g key={day.date}>
            {sysH > 0 && (
              <rect x={x} y={base - sysH} width={barWidth} height={sysH} rx="1" className="fill-ai-sys" />
            )}
            {ownH > 0 && (
              <rect x={x} y={base - sysH - ownH} width={barWidth} height={ownH} rx="1" className="fill-ai-own" />
            )}
          </g>
        )
      })}
    </svg>
  )
}

export default function AiKeysManager({ ai }: { ai: AiProps }) {
  const [editing, setEditing] = useState<{ provider: AiProvider; existing: AiCredential | null } | null>(null)
  const [apiKey, setApiKey] = useState('')
  const [apiBase, setApiBase] = useState('')
  const [modelId, setModelId] = useState('')
  const [saving, setSaving] = useState(false)

  const byProvider = new Map(ai.credentials.map((c) => [c.provider, c]))

  const open = (provider: AiProvider) => {
    const existing = byProvider.get(provider) ?? null
    setEditing({ provider, existing })
    setApiKey('')
    setApiBase(existing?.api_base ?? '')
    setModelId(existing?.model_id ?? ai.model_options[provider]?.[0]?.id ?? '')
  }

  const close = () => {
    setEditing(null)
    setApiKey('')
    setApiBase('')
    setModelId('')
  }

  // 「测试并保存」是唯一的保存路径 —— 服务端会先拿这把 key 真打一次握手，
  // 通了才写库。所以这里的 loading 是真的在等一次网络往返。
  const submit = () => {
    if (!editing) return
    if (!apiKey.trim()) {
      message.warning('请填 API Key')
      return
    }
    if (!modelId.trim()) {
      message.warning('请选一个模型')
      return
    }

    const payload = {
      provider: editing.provider,
      api_key: apiKey.trim(),
      model_id: modelId.trim(),
      api_base: editing.provider === 'custom' ? apiBase.trim() : '',
    }

    setSaving(true)
    const done = { preserveScroll: true, onSuccess: close, onFinish: () => setSaving(false) }
    if (editing.existing) {
      router.patch(`/settings/ai_credentials/${editing.existing.id}`, payload, done)
    } else {
      router.post('/settings/ai_credentials', payload, done)
    }
  }

  const remove = (credential: AiCredential) => {
    Modal.confirm({
      title: `删除 ${PROVIDER_META[credential.provider].name} 的 key？`,
      content: '删掉之后这一家就不可用了。已经产生的用量记录不会删。',
      okText: '删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: () => router.delete(`/settings/ai_credentials/${credential.id}`, { preserveScroll: true }),
    })
  }

  const makeDefault = (credential: AiCredential) =>
    router.post(`/settings/ai_credentials/${credential.id}/default`, {}, { preserveScroll: true })

  const modelOptions: AiModelOption[] = editing ? (ai.model_options[editing.provider] ?? []) : []

  return (
    <div>
      <div className="mb-4">
        <h2 className="m-0 text-xl font-medium text-gray-900">AI 能力</h2>
        <p className="mb-0 mt-1 text-sm text-gray-500">
          服务端替你调 AI 时用哪个 key、今天还能花多少、已经花了多少
        </p>
      </div>

      <AiQuotaBar quota={ai.quota} onAddKey={() => open('openai')} />

      {/* 固定几格而不是一张表：槽位数量是设计决定的（四家原生 + 一个兼容口），
          不是数据决定的。表格在「只配了一家」时是四行空单元格。 */}
      <div className="grid gap-3 sm:grid-cols-2">
        {ai.providers.map((provider) => {
          const credential = byProvider.get(provider)
          const meta = PROVIDER_META[provider]

          return (
            <div
              key={provider}
              className={`flex min-h-[118px] flex-col gap-2 rounded-lg border p-3 ${
                credential ? 'border-gray-200 bg-white' : 'border-dashed border-gray-300 bg-gray-50'
              }`}
            >
              <div className="flex items-center gap-2">
                <span
                  className={`grid h-[22px] w-[22px] place-items-center rounded text-[11px] font-bold text-white ${meta.badgeClass}`}
                >
                  {meta.badge}
                </span>
                <span className="text-sm font-semibold text-gray-900">{meta.name}</span>
                <span className="flex-1" />
                {credential?.is_default && (
                  <span className="rounded-full bg-gray-900 px-2 py-0.5 text-[11px] font-medium text-white">默认</span>
                )}
                {credential &&
                  (credential.last_error_code ? (
                    <Tooltip title={`最近一次失败：${when(credential.last_error_at)}`}>
                      <span className="rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-700">
                        {ERROR_LABEL[credential.last_error_code] ?? '上次失败'}
                      </span>
                    </Tooltip>
                  ) : credential.last_used_at ? (
                    <span className="rounded-full border border-green-200 bg-green-50 px-2 py-0.5 text-[11px] font-medium text-green-700">
                      可用
                    </span>
                  ) : (
                    <span className="rounded-full border border-gray-200 bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-500">
                      从未用过
                    </span>
                  ))}
                {!credential && (
                  <span className="rounded-full border border-gray-200 bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-500">
                    未配置
                  </span>
                )}
              </div>

              {credential ? (
                <dl className="grid grid-cols-[56px_minmax(0,1fr)] gap-x-2 gap-y-1 text-xs">
                  <dt className="text-gray-400">Key</dt>
                  <dd className="break-all font-mono text-gray-700">{credential.api_key_hint}</dd>
                  <dt className="text-gray-400">模型</dt>
                  <dd className="break-all text-gray-700">{credential.model_id}</dd>
                  {credential.api_base && (
                    <>
                      <dt className="text-gray-400">地址</dt>
                      <dd className="break-all font-mono text-gray-700">{credential.api_base}</dd>
                    </>
                  )}
                  <dt className="text-gray-400">最近用</dt>
                  <dd className="text-gray-700">{when(credential.last_used_at)}</dd>
                </dl>
              ) : (
                <p className="mb-0 text-xs text-gray-500">{meta.hint}</p>
              )}

              <div className="mt-auto flex items-center gap-1.5">
                {credential ? (
                  <>
                    <Button size="small" onClick={() => open(provider)}>
                      换 key
                    </Button>
                    {!credential.is_default && (
                      <Button size="small" onClick={() => makeDefault(credential)}>
                        设为默认
                      </Button>
                    )}
                    <span className="flex-1" />
                    <Button size="small" type="text" danger onClick={() => remove(credential)}>
                      删除
                    </Button>
                  </>
                ) : (
                  <Button size="small" onClick={() => open(provider)}>
                    + 添加
                  </Button>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {/* 用量小结。只呈现已经发生的事实 —— 不放预测、不放「你比上月多花了」。 */}
      <div className="mt-5 border-t border-gray-200 pt-4">
        <div className="mb-3 flex items-baseline gap-2">
          <h3 className="m-0 text-sm font-semibold text-gray-900">用量</h3>
          <span className="flex-1" />
          <span className="text-xs text-gray-400">近 30 天 · 每天 00:00 结算</span>
        </div>

        <div className="mb-3 grid grid-cols-3 gap-px overflow-hidden rounded-lg border border-gray-200 bg-gray-200">
          <div className="bg-white px-3 py-2">
            <div className="text-[11px] text-gray-400">合计成本</div>
            <div className="text-lg font-semibold tabular-nums text-gray-900">
              {money(ai.quota.own_spend_30d + ai.quota.system_spend_30d)}
            </div>
            <div className="text-[11px] text-gray-500">
              {ai.quota.own_calls_30d + ai.quota.system_calls_30d} 次调用
            </div>
          </div>
          <div className="bg-white px-3 py-2">
            <div className="text-[11px] text-gray-400">你自己的 key</div>
            <div className="text-lg font-semibold tabular-nums text-ai-own">{money(ai.quota.own_spend_30d)}</div>
            <div className="text-[11px] text-gray-500">{ai.quota.own_calls_30d} 次 · 落在你的账单上</div>
          </div>
          <div className="bg-white px-3 py-2">
            <div className="text-[11px] text-gray-400">系统赠送额度</div>
            <div className="text-lg font-semibold tabular-nums text-ai-sys">{money(ai.quota.system_spend_30d)}</div>
            <div className="text-[11px] text-gray-500">{ai.quota.system_calls_30d} 次</div>
          </div>
        </div>

        <UsageChart series={ai.quota.daily_series} />
        <div className="mt-2 flex items-center gap-3 text-xs text-gray-500">
          <span>
            <i className="mr-1.5 inline-block h-2 w-2 rounded-sm bg-ai-own align-[-1px]" />
            你自己的 key
          </span>
          <span>
            <i className="mr-1.5 inline-block h-2 w-2 rounded-sm bg-ai-sys align-[-1px]" />
            系统赠送额度
          </span>
        </div>
      </div>

      <Modal
        title={
          editing
            ? `${editing.existing ? '换' : '添加'} ${PROVIDER_META[editing.provider].name} 的 key`
            : ''
        }
        open={editing !== null}
        onCancel={close}
        onOk={submit}
        confirmLoading={saving}
        okText="测试并保存"
        cancelText="取消"
      >
        {editing && (
          <div className="space-y-3 py-2">
            {editing.provider === 'custom' && (
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  API 地址 <span className="text-red-500">*</span>
                </label>
                <Input
                  value={apiBase}
                  onChange={(e) => setApiBase(e.target.value)}
                  placeholder="https://api.deepseek.com/v1"
                />
                <p className="mb-0 mt-1 text-xs text-gray-400">
                  本地 Ollama 填 <code>http://localhost:11434/v1</code>。
                </p>
              </div>
            )}

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                API Key <span className="text-red-500">*</span>
              </label>
              <Input.Password
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={editing.existing ? '粘贴新的 key 以替换' : ''}
                autoComplete="off"
              />
              <p className="mb-0 mt-1 text-xs text-gray-400">{PROVIDER_META[editing.provider].hint}</p>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                模型 {editing.provider === 'custom' && <span className="text-red-500">*</span>}
              </label>
              {editing.provider === 'custom' ? (
                <>
                  <Input value={modelId} onChange={(e) => setModelId(e.target.value)} placeholder="deepseek-chat" />
                  <p className="mb-0 mt-1 text-xs text-gray-400">
                    兼容口的模型 id <b>手填</b> —— 它不一定在模型注册表里。
                  </p>
                </>
              ) : (
                <Select
                  className="w-full"
                  value={modelId || undefined}
                  onChange={setModelId}
                  options={modelOptions.map((option) => ({
                    value: option.id,
                    label:
                      option.input_price !== null
                        ? `${option.label} — 入 $${option.input_price}/M · 出 $${option.output_price}/M`
                        : option.label,
                  }))}
                />
              )}
            </div>

            <div className="rounded-md bg-blue-50 px-3 py-2 text-xs text-gray-700">
              🔒 key 加密存库。<b>存进去之后谁都看不到完整值</b> —— 包括后台的 admin，也包括你自己。
            </div>

            {/* 这条披露是必须的，不是客套：算不出成本的模型不进按钱的闸门。
                不写出来，用户会以为兼容口也受同一个上限保护。 */}
            {editing.provider === 'custom' && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-gray-700">
                ⚠ 兼容口<b>只能用自己的 key</b>，不消耗系统赠送额度。模型不在注册表里时算不出成本，
                用量里只有 token 数、没有金额。
              </div>
            )}

            <p className="mb-0 text-xs text-gray-500">
              点「测试并保存」会先拿这把 key 真打一次最小的握手调用，通了才存 ——
              配错的 key 存进去只会在下次真正要用的时候才炸。
            </p>
          </div>
        )}
      </Modal>
    </div>
  )
}
